/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Icon,
  Link,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import moment from 'moment';

import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';

import { invertedMessages } from './messages';

import './CertsListSecondaryHeader.css';

function CertsListSecondaryHeader() {
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(false);

  const {
    core: {
      campaignInfo: {
        campaignName,
        campaignType,
        startDate,
        endDate,
        autoRevoke,
        campaignOwnerName,
      },
      isAdminLogin,
    },
    global: { isWithoutCampaignKey, globalDateFormat } = {},
  } = useSelector((state) => state?.certifications?.list);

  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);

  const { isMenuPinned } = useSelector((state) => state?.user);

  const textClasses = classnames(
    'CertsListSecondaryHeader-text',
    !isAdminLogin && 'CertsListSecondaryHeader-text--endUser'
  );

  const titleClasses = classnames(
    'CertsListSecondaryHeader-title',
    !isAdminLogin && 'CertsListSecondaryHeader-title--endUser',
    campaignName?.length > 10 && 'CertsListSecondaryHeader-title--long'
  );

  const chevronClasses = classnames(
    'CertsListSecondaryHeader-chevronLeft',
    isMenuPinned && 'CertsListSecondaryHeader-chevronLeft--isMenuPinned'
  );

  const handleBackClick = () => {
    window.location.replace('/ECM/campaign/list');
  };

  const getBackButton = () => {
    if (!isAdminLogin || isWithoutCampaignKey) {
      return false;
    }

    return true;
  };

  const handleDetailsClick = () => {
    setIsDetailsExpanded(!isDetailsExpanded);
  };

  const getMessage = () => {
    if (isAdminLogin && !isWithoutCampaignKey) {
      return null;
    }

    if (isWithoutCampaignKey) {
      return (
        messages.CERTS_LIST_END_USER_MESSAGE ||
        localize(intl, getMessageKey('CERTS_LIST_END_USER_MESSAGE'))
      );
    }

    return (
      messages.CERTS_LIST_END_USER_MESSAGE ||
      localize(intl, getMessageKey('CERTS_LIST_END_USER_MESSAGE'))
    );
  };

  const renderAttributes = () => {
    const entries = [
      {
        key:
          messages.CERTS_LIST_TYPE ||
          localize(intl, getMessageKey('CERTS_LIST_TYPE')),
        value: campaignType || '',
      },
      {
        key:
          messages.CERTS_LIST_START_DATE ||
          localize(intl, getMessageKey('CERTS_LIST_START_DATE')),
        value: startDate || '',
      },
      {
        key:
          messages.CERTS_LIST_DUE_DATE ||
          localize(intl, getMessageKey('CERTS_LIST_DUE_DATE')),
        value: endDate || '',
      },
      {
        key:
          messages.CERTS_LIST_AUTO_REVOKE ||
          localize(intl, getMessageKey('CERTS_LIST_AUTO_REVOKE')),
        value: autoRevoke ? l('On') : l('Off'),
      },
      {
        key:
          messages.CERTS_LIST_CAMPAIGN_OWNER ||
          localize(intl, getMessageKey('CERTS_LIST_CAMPAIGN_OWNER')),
        value: campaignOwnerName || '',
      },
    ];

    const dateFields = ['Start Date', 'Due Date'];

    return entries.map(({ key, value }, index) => {
      const formattedKey = key.charAt(0).toUpperCase() + key.slice(1);
      const isLastItem = index === entries.length - 1;
      const isAutoRevoke = key === 'Auto Revoke';

      const isDateField = dateFields.includes(key);

      return (
        <React.Fragment key={key}>
          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color='--color-foreground-neutral-medium'
            className='CertsListSecondaryHeader-textKey'>
            {`${formattedKey}:`}
          </Typography>

          <Typography
            kind='body2'
            desktop='body2-d'
            mobile='body2-d'
            color='--color-foreground-secondary-bold'>
            <span className='CertsListSecondaryHeader-textValue'>
              {(isDateField
                ? moment(value, 'DD/MM/YYYY')
                    .local()
                    .format(`${globalDateFormat?.toUpperCase()}`)
                : value) || ''}
              {isAutoRevoke && autoRevoke && (
                <Tooltip
                  maxWidth='12rem'
                  placement='right-start'
                  isPortalEnabled
                  trigger={
                    <Icon
                      kind='AlertInfoFilledSeparateColors'
                      color='--color-foreground-info-medium'
                      size='small'
                      className='CertsListSecondaryHeader-infoIcon'
                    />
                  }
                  text='If the certification is not submitted by the due date, some access will be revoked automatically'
                />
              )}
            </span>
          </Typography>

          <Typography kind='body2' desktop='body2-d' mobile='body2-d'>
            {!isLastItem && (
              <span className='CertsListSecondaryHeader-dotSeparator'>•</span>
            )}
          </Typography>
        </React.Fragment>
      );
    });
  };

  return (
    <Box className='CertsListSeconaryHeader-wrapper'>
      <Box className='CertsListSecondaryHeader-content'>
        <Box className='CertsListSecondaryHeader'>
          {getBackButton() && (
            <ButtonIcon
              icon={<Icon kind='ChevronLeft' />}
              className={chevronClasses}
              kind='ghost'
              color='neutral'
              onClick={handleBackClick}
              size='large'
            />
          )}

          <Typography kind='h1' className={titleClasses} mobile='h1-d'>
            <span className='CertsListSecondaryHeader-title--certName'>
              {isAdminLogin
                ? campaignName ||
                  messages.CERTS_LIST_HEADER ||
                  localize(intl, getMessageKey('CERTS_LIST_HEADER'))
                : messages.CERTS_LIST_HEADER ||
                  localize(intl, getMessageKey('CERTS_LIST_HEADER'))}
            </span>

            {isAdminLogin && !isWithoutCampaignKey && (
              <div
                tabIndex='0'
                role='button'
                className='CertsListSecondaryHeader-chevronToggleWrapper'
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleDetailsClick();
                  }
                }}>
                <Link
                  suffixIconKind='ChevronDownSmall'
                  kind='button'
                  className='CertsListSecondaryHeader-chevronToggle'
                  text={
                    messages.CERTS_DETALS_LABEL ||
                    localize(intl, getMessageKey('CERTS_DETALS_LABEL'))
                  }
                  suffixIconRotate={isDetailsExpanded ? '180' : null}
                  onClick={handleDetailsClick}
                  tabIndex={-1}
                />
              </div>
            )}
          </Typography>
        </Box>

        {isAdminLogin && isDetailsExpanded && (
          <Box className='CertsListSecondaryHeader-attributes'>
            {renderAttributes()}
          </Box>
        )}
      </Box>

      <Box className={textClasses}>
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color='--color-foreground-neutral-medium'>
          {getMessage()}
        </Typography>
      </Box>
    </Box>
  );
}

export default CertsListSecondaryHeader;
